/**
 * Vendors Dashboard
 *
 * Main dashboard for Vendor Management domain.
 * Shows real-time stats, quick actions, and recent activity.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconBuildingStore,
  IconPackage,
  IconCalendarEvent,
  IconStar,
  IconArrowRight,
  IconClock,
  IconCheck,
  IconAlertTriangle,
  IconUsers,
  IconStarFilled,
  IconMessage,
  IconRefresh,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Grid,
  Group,
  Badge,
  Button,
  Text,
  Title,
  ThemeIcon,
  Paper,
  Skeleton,
  Table,
  Progress,
  SimpleGrid,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { VendorStatusBadge, ProductStatusBadge, EventStatusBadge } from '../components';
import {
  useVendorService,
  useVendorProductService,
  useEventService,
  useVendorRatingService,
  useVendorResponseService,
} from '@actschurch/ccms-sdk';

export default function VendorsDashboard() {
  const navigate = useNavigate();
  const vendorService = useVendorService();
  const productService = useVendorProductService();
  const eventService = useEventService();
  const ratingService = useVendorRatingService();
  const responseService = useVendorResponseService();

  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Fetch all data
  const { data: vendors = [], isLoading: vendorsLoading } = useQuery({
    queryKey: ['vendors', 'list'],
    queryFn: () => vendorService.getViews(),
  });

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['vendor-products', 'list'],
    queryFn: () => productService.getViews(),
  });

  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['events', 'list'],
    queryFn: () => eventService.listView(),
  });

  const { data: ratings = [], isLoading: ratingsLoading } = useQuery({
    queryKey: ['vendor-ratings', 'all'],
    queryFn: () => ratingService.getViews(),
  });

  const { data: responses = [] } = useQuery({
    queryKey: ['vendor-responses', 'all'],
    queryFn: () => responseService.getAll(),
  });

  // Refresh all dashboard data
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['vendors', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['vendor-products', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['events', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['vendor-ratings', 'all'] }),
      queryClient.invalidateQueries({ queryKey: ['vendor-responses', 'all'] }),
    ]);
    setLastRefresh(new Date());
    setIsRefreshing(false);
  };

  // Format last refresh time
  const formatLastRefresh = (date: Date | null) => {
    if (!date) return null;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Calculate stats
  const stats = useMemo(() => {
    const pendingVendors = vendors.filter((v) => v.vendorStatusID === 1 || v.vendorStatusID === 2).length; // Submitted or Under Review
    const activeVendors = vendors.filter((v) => v.vendorStatusID === 4).length; // Active
    const draftProducts = products.filter((p) => p.statusID === -1).length;
    const activeProducts = products.filter((p) => p.statusID === 1).length;
    const upcomingEvents = events.filter((e) => e.isUpcoming).length;
    const ongoingEvents = events.filter((e) => e.isOngoing).length;
    const pendingResponses = ratings.filter(
      (r) => !responses.some((resp) => resp.ratingID === r.ratingID)
    ).length;
    const avgRating =
      ratings.length > 0
        ? ratings.reduce(
            (sum, r) => sum + (r.overallRating + r.qualityRating + r.serviceRating + r.valueRating) / 4,
            0
          ) / ratings.length
        : 0;

    return {
      totalVendors: vendors.length,
      pendingVendors,
      activeVendors,
      totalProducts: products.length,
      draftProducts,
      activeProducts,
      totalEvents: events.length,
      upcomingEvents,
      ongoingEvents,
      totalRatings: ratings.length,
      pendingResponses,
      avgRating,
    };
  }, [vendors, products, events, ratings, responses]);

  // Recent vendors (last 5)
  const recentVendors = useMemo(() => {
    return [...vendors]
      .sort((a, b) => (b.createdDateTime || 0) - (a.createdDateTime || 0))
      .slice(0, 5);
  }, [vendors]);

  // Pending approvals (vendors and products)
  const pendingApprovals = useMemo(() => {
    const pendingVendorList = vendors
      .filter((v) => v.vendorStatusID === 1 || v.vendorStatusID === 2) // Submitted or Under Review
      .slice(0, 3)
      .map((v) => ({
        type: 'vendor' as const,
        id: v.vendorID,
        name: v.businessName || `Vendor #${v.vendorID}`,
        date: v.createdDateTime,
      }));

    const pendingProductList = products
      .filter((p) => p.statusID === -1)
      .slice(0, 3)
      .map((p) => ({
        type: 'product' as const,
        id: p.productID,
        name: p.name || `Product #${p.productID}`,
        date: p.createdDateTime,
      }));

    return [...pendingVendorList, ...pendingProductList]
      .sort((a, b) => (b.date || 0) - (a.date || 0))
      .slice(0, 5);
  }, [vendors, products]);

  // Upcoming events
  const upcomingEventsList = useMemo(() => {
    return events
      .filter((e) => e.isUpcoming || e.isOngoing)
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .slice(0, 5);
  }, [events]);

  const isLoading = vendorsLoading || productsLoading || eventsLoading || ratingsLoading;

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={40} width={300} />
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={120} />
          ))}
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <Skeleton height={300} />
          <Skeleton height={300} />
        </SimpleGrid>
      </Stack>
    );
  }

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Title order={2}>Vendor Management</Title>
            <Text size="sm" c="dimmed">
              Overview of vendors, products, events, and ratings
            </Text>
          </div>
          <Group gap="sm" align="center">
            {lastRefresh && (
              <Text size="xs" c="dimmed">
                Last updated: {formatLastRefresh(lastRefresh)}
              </Text>
            )}
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={handleRefresh}
              loading={isRefreshing}
              size="sm"
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Main Stats */}
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          <Paper
            withBorder
            p="md"
            radius="md"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/app/vendors/list')}
          >
            <Group justify="space-between" mb="xs">
              <ThemeIcon size="lg" radius="md" variant="light" color="blue">
                <IconBuildingStore size={20} />
              </ThemeIcon>
              {stats.pendingVendors > 0 && (
                <Badge color="orange" variant="filled" size="sm">
                  {stats.pendingVendors} pending
                </Badge>
              )}
            </Group>
            <Text size="xl" fw={700}>
              {stats.totalVendors}
            </Text>
            <Text size="sm" c="dimmed">
              Total Vendors
            </Text>
            <Progress
              value={(stats.activeVendors / Math.max(stats.totalVendors, 1)) * 100}
              size="xs"
              color="green"
              mt="xs"
            />
            <Text size="xs" c="dimmed" mt={4}>
              {stats.activeVendors} active
            </Text>
          </Paper>

          <Paper
            withBorder
            p="md"
            radius="md"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/app/vendors/products')}
          >
            <Group justify="space-between" mb="xs">
              <ThemeIcon size="lg" radius="md" variant="light" color="green">
                <IconPackage size={20} />
              </ThemeIcon>
              {stats.draftProducts > 0 && (
                <Badge color="orange" variant="filled" size="sm">
                  {stats.draftProducts} draft
                </Badge>
              )}
            </Group>
            <Text size="xl" fw={700}>
              {stats.totalProducts}
            </Text>
            <Text size="sm" c="dimmed">
              Total Products
            </Text>
            <Progress
              value={(stats.activeProducts / Math.max(stats.totalProducts, 1)) * 100}
              size="xs"
              color="green"
              mt="xs"
            />
            <Text size="xs" c="dimmed" mt={4}>
              {stats.activeProducts} active
            </Text>
          </Paper>

          <Paper
            withBorder
            p="md"
            radius="md"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/app/vendors/events')}
          >
            <Group justify="space-between" mb="xs">
              <ThemeIcon size="lg" radius="md" variant="light" color="violet">
                <IconCalendarEvent size={20} />
              </ThemeIcon>
              {stats.ongoingEvents > 0 && (
                <Badge color="green" variant="filled" size="sm">
                  {stats.ongoingEvents} live
                </Badge>
              )}
            </Group>
            <Text size="xl" fw={700}>
              {stats.totalEvents}
            </Text>
            <Text size="sm" c="dimmed">
              Total Events
            </Text>
            <Text size="xs" c="blue" mt="xs">
              {stats.upcomingEvents} upcoming
            </Text>
          </Paper>

          <Paper
            withBorder
            p="md"
            radius="md"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/app/vendors/ratings')}
          >
            <Group justify="space-between" mb="xs">
              <ThemeIcon size="lg" radius="md" variant="light" color="yellow">
                <IconStarFilled size={20} />
              </ThemeIcon>
              {stats.pendingResponses > 0 && (
                <Badge color="orange" variant="filled" size="sm">
                  {stats.pendingResponses} pending
                </Badge>
              )}
            </Group>
            <Group gap="xs" align="baseline">
              <Text size="xl" fw={700}>
                {stats.avgRating.toFixed(1)}
              </Text>
              <Text size="sm" c="dimmed">
                / 5.0
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              Average Rating
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              {stats.totalRatings} total reviews
            </Text>
          </Paper>
        </SimpleGrid>

        <Grid>
          {/* Recent Vendors */}
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Card withBorder padding="md" radius="md" h="100%">
              <Group justify="space-between" mb="md">
                <Group gap="sm">
                  <ThemeIcon size="md" radius="md" variant="light" color="blue">
                    <IconBuildingStore size={16} />
                  </ThemeIcon>
                  <Text fw={600}>Recent Vendors</Text>
                </Group>
                <Button
                  variant="subtle"
                  size="xs"
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => navigate('/app/vendors/list')}
                >
                  View All
                </Button>
              </Group>

              {recentVendors.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="lg">
                  No vendors yet
                </Text>
              ) : (
                <Table>
                  <Table.Tbody>
                    {recentVendors.map((vendor) => (
                      <Table.Tr
                        key={vendor.vendorID}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/app/vendors/view/${vendor.vendorID}`)}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {vendor.businessName || `Vendor #${vendor.vendorID}`}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {vendor.tradingName}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <VendorStatusBadge status={vendor.vendorStatusID} size="xs" />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Card>
          </Grid.Col>

          {/* Pending Approvals */}
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Card withBorder padding="md" radius="md" h="100%">
              <Group justify="space-between" mb="md">
                <Group gap="sm">
                  <ThemeIcon size="md" radius="md" variant="light" color="orange">
                    <IconClock size={16} />
                  </ThemeIcon>
                  <Text fw={600}>Pending Approvals</Text>
                </Group>
                <Badge color="orange" variant="light">
                  {pendingApprovals.length}
                </Badge>
              </Group>

              {pendingApprovals.length === 0 ? (
                <Stack align="center" py="lg">
                  <ThemeIcon size="xl" radius="xl" variant="light" color="green">
                    <IconCheck size={24} />
                  </ThemeIcon>
                  <Text c="dimmed" size="sm">
                    All caught up!
                  </Text>
                </Stack>
              ) : (
                <Stack gap="xs">
                  {pendingApprovals.map((item) => (
                    <Paper
                      key={`${item.type}-${item.id}`}
                      withBorder
                      p="sm"
                      radius="sm"
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        navigate(
                          item.type === 'vendor'
                            ? `/app/vendors/view/${item.id}`
                            : `/app/vendors/products/${item.id}`
                        )
                      }
                    >
                      <Group justify="space-between">
                        <Group gap="xs">
                          {item.type === 'vendor' ? (
                            <IconBuildingStore size={14} color="var(--mantine-color-blue-6)" />
                          ) : (
                            <IconPackage size={14} color="var(--mantine-color-green-6)" />
                          )}
                          <Text size="sm">{item.name}</Text>
                        </Group>
                        <Badge variant="light" color="orange" size="xs">
                          Pending
                        </Badge>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Card>
          </Grid.Col>

          {/* Upcoming Events */}
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Card withBorder padding="md" radius="md" h="100%">
              <Group justify="space-between" mb="md">
                <Group gap="sm">
                  <ThemeIcon size="md" radius="md" variant="light" color="violet">
                    <IconCalendarEvent size={16} />
                  </ThemeIcon>
                  <Text fw={600}>Upcoming Events</Text>
                </Group>
                <Button
                  variant="subtle"
                  size="xs"
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => navigate('/app/vendors/events')}
                >
                  View All
                </Button>
              </Group>

              {upcomingEventsList.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="lg">
                  No upcoming events
                </Text>
              ) : (
                <Stack gap="xs">
                  {upcomingEventsList.map((event) => (
                    <Paper
                      key={event.eventID}
                      withBorder
                      p="sm"
                      radius="sm"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/app/vendors/events/${event.eventID}`)}
                    >
                      <Group justify="space-between">
                        <Stack gap={2}>
                          <Text size="sm" fw={500}>
                            {event.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {event.dateRangeDisplay}
                          </Text>
                        </Stack>
                        <Badge
                          variant="light"
                          color={event.isOngoing ? 'green' : 'blue'}
                          size="sm"
                        >
                          {event.isOngoing ? 'Ongoing' : `${event.daysUntilStart} days`}
                        </Badge>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Card>
          </Grid.Col>

          {/* Ratings Overview */}
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Card withBorder padding="md" radius="md" h="100%">
              <Group justify="space-between" mb="md">
                <Group gap="sm">
                  <ThemeIcon size="md" radius="md" variant="light" color="yellow">
                    <IconStar size={16} />
                  </ThemeIcon>
                  <Text fw={600}>Ratings Overview</Text>
                </Group>
                <Button
                  variant="subtle"
                  size="xs"
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => navigate('/app/vendors/ratings')}
                >
                  View All
                </Button>
              </Group>

              <SimpleGrid cols={2}>
                <Paper withBorder p="sm" radius="sm">
                  <Group gap="sm">
                    <ThemeIcon size="md" radius="md" variant="light" color="yellow">
                      <IconStarFilled size={16} />
                    </ThemeIcon>
                    <Stack gap={0}>
                      <Text size="lg" fw={700}>
                        {stats.avgRating.toFixed(1)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Average
                      </Text>
                    </Stack>
                  </Group>
                </Paper>
                <Paper withBorder p="sm" radius="sm">
                  <Group gap="sm">
                    <ThemeIcon size="md" radius="md" variant="light" color="blue">
                      <IconUsers size={16} />
                    </ThemeIcon>
                    <Stack gap={0}>
                      <Text size="lg" fw={700}>
                        {stats.totalRatings}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Total
                      </Text>
                    </Stack>
                  </Group>
                </Paper>
                <Paper withBorder p="sm" radius="sm">
                  <Group gap="sm">
                    <ThemeIcon size="md" radius="md" variant="light" color="green">
                      <IconMessage size={16} />
                    </ThemeIcon>
                    <Stack gap={0}>
                      <Text size="lg" fw={700}>
                        {stats.totalRatings - stats.pendingResponses}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Responded
                      </Text>
                    </Stack>
                  </Group>
                </Paper>
                <Paper withBorder p="sm" radius="sm">
                  <Group gap="sm">
                    <ThemeIcon size="md" radius="md" variant="light" color="orange">
                      <IconAlertTriangle size={16} />
                    </ThemeIcon>
                    <Stack gap={0}>
                      <Text size="lg" fw={700}>
                        {stats.pendingResponses}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Pending
                      </Text>
                    </Stack>
                  </Group>
                </Paper>
              </SimpleGrid>
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </FadeIn>
  );
}
